import { Avatar, Box, Sheet, Typography } from '@mui/joy';
import { UsersThree } from '@phosphor-icons/react';
import { mediaUrl } from '../../../utils/mediaUrl';

/**
 * Подсветка совпадения.
 *
 * Ищем без учёта регистра, но выводим исходный текст: приводить сам фрагмент к
 * нижнему регистру нельзя — это исказит сообщение.
 */
function HighlightedText({ text = '', query = '' }) {
    const needle = query.trim().toLowerCase();
    if (!needle) return text;

    const index = text.toLowerCase().indexOf(needle);
    if (index === -1) return text;

    return (
        <>
            {text.slice(0, index)}
            <Box
                component="mark"
                sx={{
                    px: '2px',
                    borderRadius: '4px',
                    color: 'common.white',
                    backgroundColor: 'primary.500',
                }}
            >
                {text.slice(index, index + needle.length)}
            </Box>
            {text.slice(index + needle.length)}
        </>
    );
}

/**
 * Строка выдачи поиска по переписке: где нашли, кто написал и фрагмент текста.
 *
 * Совпадение внутри фрагмента выделяем — иначе в длинной строке непонятно, за
 * что зацепился поиск.
 *
 * @param {Object} item - совпадение от `/api/messages/search`
 * @param {string} query - запрос, по которому подсвечиваем
 * @param {Function} onClick
 */
export default function MessageSearchItem({ item, query = '', onClick }) {
    const title = item?.isGroup
        ? (item?.chatName || 'Групповой чат')
        : [item?.companion?.firstName, item?.companion?.lastName].filter(Boolean).join(' ') || 'Диалог';

    // В группе важно, кто именно написал; в личном чате это и так очевидно.
    const senderPrefix = item?.isGroup && item?.sender ? `${item.sender.firstName}: ` : '';

    const imageUrl = (src) => mediaUrl(src) ?? undefined;

    return (
        <Sheet
            variant="plain"
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                gap: 1.5,
                mb: 0.5,
                cursor: 'pointer',
                borderRadius: '16px',
                '&:hover': {
                    backgroundColor: 'neutral.softHoverBg',
                },
            }}
        >
            {/* Как в списке чатов: у группы сначала пробуем её картинку и только
                без неё рисуем круг с иконкой. */}
            {item?.isGroup && !item?.chatImage ? (
                <Box sx={(theme) => ({
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: '12px',
                    backgroundColor: theme.palette.primary[500],
                })}>
                    <UsersThree size={20} color="white" weight="fill" />
                </Box>
            ) : (
                <Avatar
                    src={imageUrl(item?.isGroup ? item?.chatImage : item?.companion?.avatar)}
                    alt={title}
                    sx={{ flexShrink: 0, ...(item?.isGroup && { borderRadius: '12px' }) }}
                />
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="title-md" noWrap>
                    {title}
                </Typography>
                <Typography
                    level="body-sm"
                    color="neutral"
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {senderPrefix}
                    <HighlightedText text={item?.snippet ?? ''} query={query} />
                </Typography>
            </Box>
        </Sheet>
    );
}
